import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';
import { api } from '@/utils/api';
import {
  ClipboardCheck, Check, X, RefreshCw, Play, Loader2, Inbox,
  ShieldCheck, AlertTriangle, History, ChevronRight, FileSignature,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Action Center — operational task queue + Workflow Engine
//
// Status lifecycle:
//   open → in_progress → done | dismissed
//   pending_approval → approved | rejected  (workflow chain)
//   any → escalated  (SLA breach)
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { id: 'open',             en: 'Open',             ar: 'مفتوحة' },
  { id: 'pending_approval', en: 'Pending approval', ar: 'بانتظار الموافقة' },
  { id: 'in_progress',      en: 'In progress',      ar: 'قيد التنفيذ' },
  { id: 'approved',         en: 'Approved',         ar: 'موافق عليها' },
  { id: 'done',             en: 'Done',             ar: 'مكتملة' },
  { id: 'dismissed',        en: 'Dismissed',        ar: 'مرفوضة' },
];

const CATEGORY_TONE = {
  revenue:     { dot: '#00A86B', label: { en: 'Revenue',     ar: 'إيرادات' } },
  collections: { dot: '#D14334', label: { en: 'Collections', ar: 'تحصيل' } },
  leasing:     { dot: '#1FD18A', label: { en: 'Leasing',     ar: 'تأجير' } },
  operations:  { dot: '#D17A00', label: { en: 'Operations',  ar: 'عمليات' } },
  renewal:     { dot: '#1F66E5', label: { en: 'Renewal',     ar: 'تجديد' } },
  capex:       { dot: '#7B3FE4', label: { en: 'CAPEX',       ar: 'استثمار' } },
  general:     { dot: '#4A5263', label: { en: 'General',     ar: 'عام' } },
};

const PRIORITY_TONE = {
  high:   { en: 'High',   ar: 'عالي',  tone: '#D14334' },
  medium: { en: 'Medium', ar: 'متوسط', tone: '#D17A00' },
  low:    { en: 'Low',    ar: 'منخفض', tone: '#71777E' },
};

function ApprovalChain({ stages, isAr }) {
  if (!stages || stages.length === 0) return null;
  return (
    <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="approval-chain">
      {stages.map((s, i) => {
        const tone =
          s.status === 'approved' ? '#00A86B' :
          s.status === 'rejected' ? '#D14334' : '#9CA3AF';
        const icon =
          s.status === 'approved' ? <Check className="h-2.5 w-2.5" /> :
          s.status === 'rejected' ? <X className="h-2.5 w-2.5" /> : <span className="text-[8px] reva-mono font-bold">{s.stage}</span>;
        return (
          <React.Fragment key={s.stage}>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] reva-mono"
              style={{ background: `${tone}1A`, color: tone, border: `1px solid ${tone}33` }}
              title={`${s.label} · ${s.status}`}
              data-testid={`stage-${s.stage}-${s.status}`}
            >
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full" style={{ background: `${tone}30` }}>{icon}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </span>
            {i < stages.length - 1 && <ChevronRight className="h-3 w-3 text-[#9CA3AF]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function AuditDrawer({ open, onClose, actionId, isAr }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !actionId) return;
    setLoading(true);
    api.getActionAudit(actionId)
      .then(r => setLog(r.data.audit_log || []))
      .catch(() => setLog([]))
      .finally(() => setLoading(false));
  }, [open, actionId]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="audit-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" />
      <div className="w-full max-w-md bg-white border-s border-[#E1E6EC] p-5 overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-[#4A5263]" />
            <h3 className="text-[13px] font-semibold text-[#0E1320]">{isAr ? 'سجل التدقيق' : 'Audit log'}</h3>
          </div>
          <button onClick={onClose} className="text-[#71777E] hover:text-[#0E1320]" data-testid="audit-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-1 text-[10.5px] reva-mono text-[#9CA3AF]">{actionId}</div>
        {loading ? (
          <div className="mt-6 text-[12px] text-[#71777E]"><Loader2 className="h-4 w-4 animate-spin inline" /> {isAr ? 'جارٍ التحميل...' : 'Loading...'}</div>
        ) : (
          <ol className="mt-5 relative ms-3 border-s border-[#E1E6EC]">
            {log.map(ev => (
              <li key={ev.id} className="ms-4 pb-4" data-testid={`audit-event-${ev.event}`}>
                <span className="absolute -start-1.5 w-3 h-3 rounded-full bg-white border-2 border-[color:var(--reva-signal)]" />
                <div className="text-[12px] font-medium text-[#0E1320]">{ev.event}</div>
                {ev.detail && <div className="text-[11.5px] text-[#4A5263] mt-0.5">{ev.detail}</div>}
                <div className="text-[10px] reva-mono text-[#9CA3AF] mt-1">{ev.actor} · {new Date(ev.at).toLocaleString()}</div>
              </li>
            ))}
            {log.length === 0 && <li className="ms-4 text-[12px] text-[#71777E]">{isAr ? 'لا توجد أحداث.' : 'No events yet.'}</li>}
          </ol>
        )}
      </div>
    </div>
  );
}

function NewRenewalModal({ open, onClose, onCreated, isAr }) {
  const [leases, setLeases] = useState([]);
  const [leaseId, setLeaseId] = useState('');
  const [newRent, setNewRent] = useState('');
  const [duration, setDuration] = useState(12);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    api.getLeases?.()
      .then(r => setLeases((r.data || []).slice(0, 50)))
      .catch(() => setLeases([]));
  }, [open]);

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api.createLeaseRenewal({
        lease_id: leaseId,
        renewal_terms: { new_rent: parseFloat(newRent) || 0, duration_months: parseInt(duration) || 12 },
        note: note || null,
      });
      onCreated?.();
      onClose();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="renewal-modal">
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl border border-[#E1E6EC] w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-[#0E1320] inline-flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-[color:var(--reva-signal)]" />
            {isAr ? 'إنشاء سير عمل تجديد عقد' : 'Initiate lease renewal workflow'}
          </h3>
          <button type="button" onClick={onClose}><X className="h-4 w-4 text-[#71777E]" /></button>
        </div>
        <div>
          <label className="text-[11px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">{isAr ? 'العقد' : 'Lease'}</label>
          <select required value={leaseId} onChange={e => setLeaseId(e.target.value)} className="mt-1 w-full bg-white border border-[#E1E6EC] rounded-lg px-3 py-2 text-[13px]" data-testid="renewal-lease-select">
            <option value="">{isAr ? '— اختر عقدًا —' : '— select a lease —'}</option>
            {leases.map(l => (
              <option key={l.id} value={l.id}>
                {`${l.lease_code || (l.id || '').slice(0, 8)} · ${l.monthly_rent ?? '—'} SAR/mo`}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">{isAr ? 'الإيجار الجديد' : 'New rent (SAR/mo)'}</label>
            <input type="number" value={newRent} onChange={e => setNewRent(e.target.value)} className="mt-1 w-full bg-white border border-[#E1E6EC] rounded-lg px-3 py-2 text-[13px]" placeholder="4500" data-testid="renewal-rent-input" />
          </div>
          <div>
            <label className="text-[11px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">{isAr ? 'المدة (أشهر)' : 'Duration (months)'}</label>
            <input type="number" value={duration} onChange={e => setDuration(e.target.value)} className="mt-1 w-full bg-white border border-[#E1E6EC] rounded-lg px-3 py-2 text-[13px]" data-testid="renewal-duration-input" />
          </div>
        </div>
        <div>
          <label className="text-[11px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">{isAr ? 'ملاحظات' : 'Note (optional)'}</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="mt-1 w-full bg-white border border-[#E1E6EC] rounded-lg px-3 py-2 text-[13px]" data-testid="renewal-note-input" />
        </div>
        <div className="text-[11px] text-[#4A5263] bg-[#F7F8FA] border border-[#E1E6EC] rounded-lg p-3 inline-flex items-start gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--reva-signal)] mt-0.5 shrink-0" />
          <span>{isAr ? 'سيتم إنشاء سلسلة موافقة من 3 مراحل: مدير التأجير → مدير الأصول → المدير المالي.' : 'A 3-stage approval chain will be created: Leasing Manager → Asset Manager → Finance / CFO. SLA: 7 days.'}</span>
        </div>
        {err && <div className="text-[12px] text-[#D14334]">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-full border border-[#E1E6EC] text-[#4A5263]" data-testid="renewal-cancel">{isAr ? 'إلغاء' : 'Cancel'}</button>
          <button type="submit" disabled={busy || !leaseId} className="text-[12px] px-4 py-1.5 rounded-full bg-[color:var(--reva-signal)] text-white font-semibold disabled:opacity-50" data-testid="renewal-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin inline" /> : (isAr ? 'إنشاء' : 'Create workflow')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Tasks() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');
  const [busy, setBusy] = useState({});
  const [auditId, setAuditId] = useState(null);
  const [renewalOpen, setRenewalOpen] = useState(false);

  const fetchActions = async (status = tab) => {
    setLoading(true);
    try {
      const res = await api.listActions(status === 'all' ? null : status);
      setActions(res.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchActions(tab); /* eslint-disable-next-line */ }, [tab]);

  const handleUpdate = async (id, newStatus) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await api.updateAction(id, newStatus);
      await fetchActions(tab);
    } catch (e) { console.error(e); }
    finally { setBusy(b => ({ ...b, [id]: false })); }
  };

  const handleApprove = async (id, decision, note = null) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await api.approveAction(id, decision, note);
      await fetchActions(tab);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(b => ({ ...b, [id]: false }));
    }
  };

  return (
    <Layout>
      <div className="space-y-5" dir={isAr ? 'rtl' : 'ltr'} data-testid="tasks-page">

        {/* Header */}
        <div className="reva-card px-5 py-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[rgba(0,168,107,0.08)] border border-[rgba(0,168,107,0.20)]">
              <ClipboardCheck className="h-4 w-4 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
            </div>
            <div>
              <div className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">
                {isAr ? 'مركز الإجراءات · سير العمل' : 'Action Center · Workflow'}
              </div>
              <h1 className="font-heading text-xl text-[#0E1320]">{isAr ? 'المهام' : 'Tasks'}</h1>
            </div>
          </div>
          <div className="ms-auto flex items-center gap-2">
            <button onClick={() => setRenewalOpen(true)} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--reva-signal)] text-white hover:bg-[#00734A]" data-testid="new-renewal-btn">
              <FileSignature className="h-3.5 w-3.5" />
              <span>{isAr ? 'تجديد عقد' : 'Lease renewal'}</span>
            </button>
            <button onClick={() => fetchActions(tab)} className="reva-btn-ghost-l text-[12px] py-1.5 px-3" data-testid="tasks-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span>{isAr ? 'تحديث' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Status tabs */}
        <div className="reva-card-soft p-1 inline-flex flex-wrap" data-testid="tasks-status-tabs">
          {STATUS_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3.5 py-1.5 text-[12px] rounded-full transition-colors inline-flex items-center gap-2 ${
                tab === t.id
                  ? 'bg-[color:var(--reva-signal)] text-white font-semibold'
                  : 'text-[#4A5263] hover:text-[#0E1320]'
              }`}
              data-testid={`tasks-tab-${t.id}`}
            >
              {isAr ? t.ar : t.en}
              {tab === t.id && <span className="text-[10px] reva-mono bg-white/25 px-1.5 rounded-full">{actions.length}</span>}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="reva-card p-12 text-center text-[13px] text-[#71777E]">
            <Loader2 className="h-5 w-5 animate-spin inline" /> {isAr ? 'جارٍ التحميل...' : 'Loading...'}
          </div>
        ) : actions.length === 0 ? (
          <div className="reva-card p-12 text-center" data-testid="tasks-empty">
            <Inbox className="h-10 w-10 text-[#9CA3AF] mx-auto" strokeWidth={1.2} />
            <div className="mt-4 text-[14px] text-[#0E1320] font-medium">
              {isAr ? 'لا توجد مهام في هذه القائمة.' : `No tasks in "${STATUS_TABS.find(t => t.id === tab)?.en || tab}".`}
            </div>
            <div className="mt-2 text-[12px] text-[#71777E]">
              {isAr ? 'حوّل توصية من لوحة القيادة إلى مهمة لتظهر هنا.' : 'Convert a recommendation from the dashboard into a task — it appears here.'}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {actions.map(a => {
              const cat = CATEGORY_TONE[a.category] || CATEGORY_TONE.general;
              const pri = PRIORITY_TONE[a.priority] || PRIORITY_TONE.medium;
              const isBusy = busy[a.id];
              const escalated = a.is_escalated;
              return (
                <div key={a.id} className={`reva-card p-4 relative ${escalated ? 'border-[#D14334]/50' : ''}`} data-testid={`task-${a.id}`}>
                  {escalated && (
                    <div className="absolute top-2 end-2 inline-flex items-center gap-1 text-[10px] reva-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[#D14334]/10 text-[#D14334] border border-[#D14334]/30">
                      <AlertTriangle className="h-3 w-3" /> {isAr ? 'مُصعَّد' : 'Escalated'}
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: cat.dot }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-[#0E1320] leading-snug">{a.title}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] reva-mono">
                        <span className="text-[#71777E] uppercase tracking-[0.14em]">{isAr ? cat.label.ar : cat.label.en}</span>
                        <span style={{ color: pri.tone }} className="uppercase tracking-[0.14em]">{isAr ? pri.ar : pri.en}</span>
                        {a.impact_label && <span className="text-[#00734A] font-semibold">{a.impact_label}</span>}
                      </div>
                      {a.source && <div className="mt-1 text-[10px] reva-mono text-[#9CA3AF]">{isAr ? 'مصدر:' : 'source:'} {a.source}</div>}
                      <ApprovalChain stages={a.approval_stages} isAr={isAr} />
                    </div>
                  </div>
                  {/* Lifecycle actions */}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {a.status === 'pending_approval' && (
                      <>
                        <button onClick={() => handleApprove(a.id, 'approved')} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[color:var(--reva-signal)] text-white hover:bg-[#00734A]" data-testid={`task-approve-${a.id}`}>
                          {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} {isAr ? 'موافقة' : 'Approve'}
                        </button>
                        <button onClick={() => handleApprove(a.id, 'rejected', prompt(isAr ? 'سبب الرفض' : 'Rejection reason'))} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#D14334]/40 bg-white text-[#D14334] hover:bg-[#D14334]/5" data-testid={`task-reject-${a.id}`}>
                          <X className="h-3 w-3" /> {isAr ? 'رفض' : 'Reject'}
                        </button>
                      </>
                    )}
                    {a.status === 'open' && (
                      <>
                        <button onClick={() => handleUpdate(a.id, 'in_progress')} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[color:var(--reva-signal)] text-white hover:bg-[#00734A]" data-testid={`task-start-${a.id}`}>
                          {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} {isAr ? 'ابدأ' : 'Start'}
                        </button>
                        <button onClick={() => handleUpdate(a.id, 'dismissed')} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E1E6EC] bg-white text-[#71777E] hover:text-[#0E1320]" data-testid={`task-dismiss-${a.id}`}>
                          <X className="h-3 w-3" /> {isAr ? 'تجاهل' : 'Dismiss'}
                        </button>
                      </>
                    )}
                    {a.status === 'in_progress' && (
                      <>
                        <button onClick={() => handleUpdate(a.id, 'done')} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[color:var(--reva-signal)] text-white hover:bg-[#00734A]" data-testid={`task-done-${a.id}`}>
                          {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} {isAr ? 'تم' : 'Mark done'}
                        </button>
                        <button onClick={() => handleUpdate(a.id, 'open')} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E1E6EC] bg-white text-[#71777E] hover:text-[#0E1320]" data-testid={`task-reopen-${a.id}`}>
                          {isAr ? 'إعادة فتح' : 'Re-open'}
                        </button>
                      </>
                    )}
                    {a.status === 'approved' && (
                      <button onClick={() => handleUpdate(a.id, 'done')} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[color:var(--reva-signal)] text-white hover:bg-[#00734A]" data-testid={`task-finalize-${a.id}`}>
                        <Check className="h-3 w-3" /> {isAr ? 'إنهاء' : 'Finalize'}
                      </button>
                    )}
                    {(a.status === 'done' || a.status === 'dismissed' || a.status === 'rejected') && (
                      <button onClick={() => handleUpdate(a.id, 'open')} disabled={isBusy} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E1E6EC] bg-white text-[#71777E] hover:text-[#0E1320]" data-testid={`task-reopen-${a.id}`}>
                        {isAr ? 'إعادة فتح' : 'Re-open'}
                      </button>
                    )}
                    <button onClick={() => setAuditId(a.id)} className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E1E6EC] bg-white text-[#71777E] hover:text-[#0E1320] ms-auto" data-testid={`task-audit-${a.id}`}>
                      <History className="h-3 w-3" /> {isAr ? 'السجل' : 'Audit'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <ModuleIntelligenceBlock module="tasks" testId="tasks-intel" />
      </div>

      <AuditDrawer open={!!auditId} onClose={() => setAuditId(null)} actionId={auditId} isAr={isAr} />
      <NewRenewalModal open={renewalOpen} onClose={() => setRenewalOpen(false)} onCreated={() => fetchActions(tab)} isAr={isAr} />
    </Layout>
  );
}
